/**
 * engineNormalJson_Merge_Constants.ts - Type prefix constants for EngineNormalJson
 * Pure ES Module version
 *
 * Uses new HtmlJson format with type prefixes:
 * s=Html, k=JsonKey, K=JsonKeySkip, p=Placeholder, P=PlaceholderSkip,
 * h=Slotholder, H=SlotholderSkip, a=ArrayBlock, A=ArrayBlockSkip, x=SlotHtml, i=Slot, c=Conditional
 */

import { JsonValue, JsonValueType } from '../../../../Assembler/dist/app/json/jsonValue.js';

type PlainJsonObject = { [key: string]: unknown };
type PlainJsonMap = globalThis.Map <string, unknown>;

// Type prefix constants for new format
export const TYPE_HTML = 's';
export const TYPE_JSONKEY = 'k';
export const TYPE_JSONKEY_SKIP = 'K';
export const TYPE_PLACEHOLDER = 'p';
export const TYPE_PLACEHOLDER_SKIP = 'P';
export const TYPE_SLOTHOLDER = 'h';
export const TYPE_SLOTHOLDER_SKIP = 'H';
export const TYPE_ARRAYBLOCK = 'a';
export const TYPE_ARRAYBLOCK_SKIP = 'A';
export const TYPE_SLOTHTML = 'x';
export const TYPE_SLOT = 'i';
export const TYPE_CONDITIONAL = 'c';
export const META_KEY = '_meta';

/**
 * Finds the actual key in an object using case-insensitive matching.
 */
function findKeyCaseInsensitive(obj: PlainJsonObject | PlainJsonMap | null, key: string): string | null {
    if (!obj) return null;
    // csharp's fallback scan is string.Equals(..., OrdinalIgnoreCase) (HtmlJsonMergeUtil.cs:283): it
    // compares char-by-char with invariant casing, so it can only ever match a key of the SAME length,
    // and it allocates nothing. Guard on length first so a non-matching key costs one integer compare
    // instead of a freshly allocated lowercased string, and walk the object's own keys directly rather
    // than materializing an Object.keys() array on every lookup. Same iteration order, same result.
    const keyLower = key.toLowerCase();
    if (obj instanceof Map) {
        for (const k of obj.keys()) {
            if (k.length === key.length && k.toLowerCase() === keyLower) return k;
        }
    } else {
        for (const k in obj) {
            if (k.length === key.length && Object.prototype.hasOwnProperty.call(obj, k) && k.toLowerCase() === keyLower) return k;
        }
    }
    return null;
}

/**
 * Checks if an object (Map, JsonObject, or plain object) has a key.
 * Uses case-insensitive fallback to match Normal engine behavior.
 */
export function hasKey(obj: PlainJsonObject | PlainJsonMap | null, key: string): boolean {
    if (obj instanceof Map) {
        if (obj.has(key)) return true;
    } else if (obj && Object.prototype.hasOwnProperty.call(obj, key)) {
        return true;
    }
    return findKeyCaseInsensitive(obj, key) !== null;
}

/**
 * Unwraps a JsonValue to get the raw value
 */
export function unwrapJsonValue(jsonValue: JsonValue): string | number | boolean | unknown[] | PlainJsonObject | null {
    switch (jsonValue.typeName) {
        case JsonValueType.String:
            return jsonValue.asString();
        case JsonValueType.Number:
            return jsonValue.asNumber();
        case JsonValueType.Integer:
            return jsonValue.asInteger();
        case JsonValueType.Bool:
            return jsonValue.asBool();
        case JsonValueType.Array:
            return jsonValue.asArray();
        case JsonValueType.Object:
            return jsonValue.asObject() as unknown as PlainJsonObject;
        case JsonValueType.Null:
            return null;
        default:
            return null;
    }
}

/**
 * Gets a value from an object (Map, JsonObject, or plain object)
 * If the value is a JsonValue, returns the raw value.
 * Uses case-insensitive fallback to match Normal engine behavior.
 */
export function getValue(obj: PlainJsonObject | PlainJsonMap | null, key: string): unknown {
    let value: unknown;
    if (obj instanceof Map) {
        // Use has() before get() because JsonObject.get() returns JsonValue.null()
        // instead of undefined for missing keys, which would prevent case-insensitive fallback
        if (obj.has(key)) {
            value = obj.get(key);
        } else {
            const actualKey = findKeyCaseInsensitive(obj, key);
            if (actualKey !== null) value = obj.get(actualKey);
        }
    } else {
        value = obj ? (obj as PlainJsonObject)[key] : undefined;
        if (value === undefined && obj) {
            const actualKey = findKeyCaseInsensitive(obj, key);
            if (actualKey !== null) value = (obj as PlainJsonObject)[actualKey];
        }
    }

    // Unwrap JsonValue to get raw value
    if (value instanceof JsonValue) {
        return unwrapJsonValue(value);
    }
    return value;
}

/**
 * Checks if this is a type-prefixed key (new format: type char + digit, e.g., "k2_Title", "s1")
 * vs a regular key like "PLACEHOLDER", "MainBlock", etc.
 */
export function isTypePrefixedKey(key: string): boolean {
    return key.length >= 2 && /\d/.test(key[1]);
}
